// @ts-nocheck
import { DropDownFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import { ButtonSizeVariants } from "@insite/mobius/Button";
import { ResponsiveButtonProps } from "@store/Components/Toolkit/ResponsiveButton";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@store/Framework/Common/Utilities/dataOptions";
import { LiteralUnion } from "@toolkit/Framework/Types/Common";
import pickBy from "lodash/pickBy";
import { useMemo } from "react";

export type ButtonSizeVariantValue = ResponsiveButtonProps["sizeVariant"];
export type ButtonSizeVariantOption = DataOption<ButtonSizeVariantValue>;
export interface ButtonSizeVariantOptionMapper {
    small: ButtonSizeVariantOption;
    medium: ButtonSizeVariantOption;
    large: ButtonSizeVariantOption;
    xlarge: ButtonSizeVariantOption;
    banner: ButtonSizeVariantOption;
}
export type ButtonSizeVariantOptions = DataOptions<ButtonSizeVariantOption> & ButtonSizeVariantOptionMapper;
export type ButtonSizeVariant = LiteralUnion<keyof ButtonSizeVariantOptionMapper>;

export const buttonSizeVariantOptions: ButtonSizeVariantOptions = {
    small: { id: "small", displayName: "Small" },
    medium: { id: "medium", displayName: "Medium", isDefault: true },
    large: { id: "large", displayName: "Large" },
    xlarge: { id: "xlarge", displayName: "Very Large" },
    banner: { id: "banner", displayName: "Banner", value: ["medium", null, "large"] },
};

export type ButtonSizeVariantFieldValue = ButtonSizeVariant | undefined;
export interface ButtonSizeVariantFieldOptions<ExcludeResponsive = boolean> {
    excludeResponsive?: ExcludeResponsive;
    isChildDefinition?: boolean;
}

export const getButtonSizeVariantFieldData = (
    value: ButtonSizeVariantFieldValue,
    options?: ButtonSizeVariantFieldOptions,
) => {
    if (!value) {
        return undefined;
    }

    return getDataOptionValue(buttonSizeVariantOptions, value);
};

export const getButtonSizeVariantFieldDefinition = (name: string, options?: ButtonSizeVariantFieldOptions) => {
    const { excludeResponsive, isChildDefinition } = options || {};

    const variantOptions = excludeResponsive
        ? pickBy(buttonSizeVariantOptions, x => typeof x.value !== "object")
        : buttonSizeVariantOptions;

    const definition: DropDownFieldDefinition<string> = {
        ...getDataOptionsFieldDefinition(variantOptions),
        name,
        displayName: "Button Size",
        fieldType: !isChildDefinition ? "General" : (undefined as any),
        hideEmptyOption: true,
    };

    return definition;
};

export interface ButtonSizeVariantFieldReturnType<
    ExcludeResponsive,
    ParserReturn = (ExcludeResponsive extends true ? ButtonSizeVariants : ButtonSizeVariantValue) | undefined,
    Parser = (value: ButtonSizeVariantFieldValue) => ParserReturn,
> {
    definition: ReturnType<typeof getButtonSizeVariantFieldDefinition>;
    parse: Parser;
    useParse: Parser;
}

const getButtonSizeVariantField = <ExcludeResponsive extends boolean>(
    name: string,
    options?: ButtonSizeVariantFieldOptions<ExcludeResponsive>,
): ButtonSizeVariantFieldReturnType<ExcludeResponsive> => {
    const parse = (value: ButtonSizeVariantFieldValue) => getButtonSizeVariantFieldData(value, options) as any;

    return {
        definition: getButtonSizeVariantFieldDefinition(name, options),
        parse,
        useParse: (...parameters: Parameters<typeof parse>) => {
            return useMemo(() => parse(...parameters), parameters);
        },
    };
};

export default getButtonSizeVariantField;
